import { useState, useEffect, useCallback, RefObject } from 'react';
import { TWindowPosition, TWindowSize } from '../types';

interface UseResizableProps {
  elementRef: RefObject<HTMLElement | null>;
  containerRef: RefObject<HTMLElement | null>;
  initialSize: TWindowSize;
  minSize?: TWindowSize;
  maxSize?: TWindowSize;
  onSizeChange: (size: TWindowSize) => void;
  onPositionChange: (position: TWindowPosition) => void;
  disabled?: boolean;
}

const useResizable = ({
  elementRef,
  containerRef,
  initialSize,
  minSize = { width: 200, height: 150 },
  maxSize = { width: Infinity, height: Infinity },
  onSizeChange,
  onPositionChange,
  disabled = false,
}: UseResizableProps) => {
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [resizeDirection, setResizeDirection] = useState<string | null>(null);
  const [startPosition, setStartPosition] = useState<TWindowPosition>({ x: 0, y: 0 });
  const [startSize, setStartSize] = useState<TWindowSize>(initialSize);
  const [startWindowPosition, setStartWindowPosition] = useState<TWindowPosition>({ x: 0, y: 0 });

  const handleResizeStart = useCallback((e: React.MouseEvent, direction: string) => {
    if (disabled) return;
    
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeDirection(direction);
    setStartPosition({ x: e.clientX, y: e.clientY });
    
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      setStartSize({
        width: elementRef.current.offsetWidth,
        height: elementRef.current.offsetHeight,
      });
      setStartWindowPosition({
        x: rect.left,
        y: rect.top,
      });
    }
  }, [disabled, elementRef]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !resizeDirection || !containerRef.current) return;
    
    e.stopPropagation();
    e.preventDefault();
    
    const deltaX = e.clientX - startPosition.x;
    const deltaY = e.clientY - startPosition.y;
    
    let newWidth = startSize.width;
    let newHeight = startSize.height;
    let newX = startWindowPosition.x;
    let newY = startWindowPosition.y;

    // Handle east/west resizing
    if (resizeDirection.includes('e')) {
      newWidth = Math.max(minSize.width, Math.min(maxSize.width, startSize.width + deltaX));
    } else if (resizeDirection.includes('w')) {
      const potentialWidth = startSize.width - deltaX;
      if (potentialWidth >= minSize.width && potentialWidth <= maxSize.width) {
        newWidth = potentialWidth;
        newX = startWindowPosition.x + deltaX;
      }
    }

    // Handle north/south resizing
    if (resizeDirection.includes('s')) {
      newHeight = Math.max(minSize.height, Math.min(maxSize.height, startSize.height + deltaY));
    } else if (resizeDirection.includes('n')) {
      const potentialHeight = startSize.height - deltaY;
      if (potentialHeight >= minSize.height && potentialHeight <= maxSize.height) {
        newHeight = potentialHeight;
        newY = startWindowPosition.y + deltaY;
      }
    }

    // Update size
    onSizeChange({ width: newWidth, height: newHeight });
    
    // Update position if resizing from left or top edges
    if (resizeDirection.includes('w') || resizeDirection.includes('n')) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const relativeX = newX - containerRect.left;
      const relativeY = newY - containerRect.top;
      onPositionChange({ x: relativeX, y: relativeY });
    }
  }, [isResizing, resizeDirection, startPosition, startSize, startWindowPosition, containerRef, minSize, maxSize, onSizeChange, onPositionChange]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setResizeDirection(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
    } else {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  return {
    isResizing,
    handleResizeStart,
  };
};

export default useResizable; 